<template>
    <div class="product-select">
        <div class="search-line">
            <el-input v-model="query" class="query-input" size="mini" clearable
                      :readonly="disabled" :placeholder="inputPlaceholder"
                      @focus="openPanel" @input="queueSearch" />
            <el-button type="text" class="batch-button" icon="el-icon-document-copy" title="批量添加产品"
                       :disabled="disabled" @click.stop="handleBatch" />
        </div>
        <div v-if="panelOpen" class="result-panel">
            <div class="panel-header">
                <span>搜索结果 {{total}}</span>
                <el-select v-model="media" size="mini" clearable placeholder="全部媒体" class="media-filter"
                           popper-class="work-order-product-media-options" @change="changeMedia">
                    <el-option label="全部媒体" :value="0" />
                    <el-option v-for="plat in mediaOptions" :key="plat.plat_id"
                               :label="plat.plat_name" :value="plat.plat_id" />
                </el-select>
            </div>
            <div class="result-actions">
                <el-button type="text" :disabled="disabled || !results.length" @click="selectAll">全选当前结果</el-button>
                <el-button type="text" :disabled="disabled || !results.length" @click="unselectAll">取消全选</el-button>
            </div>
            <div class="result-list">
                <div v-if="loading" class="empty">查询中…</div>
                <template v-else-if="results.length">
                    <div v-for="product in results" :key="identity(product)" class="product-row">
                        <el-checkbox :value="isSelected(product)" :disabled="disabled"
                                     @change="toggleProduct(product, $event)">
                            <span class="product-text">
                                <span class="product-name" :title="product.product_name">{{product.product_name || '-'}}</span>
                                <span class="product-details">
                                    <span class="product-meta" :title="`ID：${product.display_id || product.product_key}`">{{meta(product)}}</span>
                                    <span v-if="product.subproject_content" class="product-package"
                                          :title="`包名：${product.subproject_content}`">包名 {{product.subproject_content}}</span>
                                </span>
                            </span>
                        </el-checkbox>
                    </div>
                </template>
                <div v-else class="empty">暂无匹配产品</div>
            </div>
            <div v-if="total > results.length" class="result-tip">仅显示前 {{results.length}} 项，请缩小搜索范围</div>
        </div>
        <div v-if="value.length" class="selected-block">
            <div class="selected-header">已选 {{value.length}} 项</div>
            <div class="selected-list">
                <div v-for="product in value" :key="identity(product)" class="selected-item">
                    <span class="product-text">
                        <span class="product-name" :title="product.product_name">{{product.product_name || '-'}}</span>
                        <span class="product-details">
                            <span class="product-meta" :title="`ID：${product.display_id || product.product_key}`">{{meta(product)}}</span>
                            <span v-if="product.subproject_content" class="product-package"
                                  :title="`包名：${product.subproject_content}`">包名 {{product.subproject_content}}</span>
                        </span>
                    </span>
                    <el-button v-if="!disabled" type="text" icon="el-icon-close" title="移除产品"
                               @click="removeProduct(product)" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import API from '@/api/workOrder/create.js';
import BaseDialog from '@/components/BaseDialog';

export default {
    props: { projectId: [String, Number], value: { type: Array, default: () => [] }, disabled: Boolean },
    data() {
        return { query: '', media: 0, mediaOptions: [], results: [], loading: false, total: 0,
            panelOpen: false, requestId: 0, searchTimer: null };
    },
    computed: {
        inputPlaceholder() {
            return this.disabled ? '产品选择已锁定' : '搜索产品名称、包名或 ID';
        },
    },
    watch: {
        projectId() {
            clearTimeout(this.searchTimer);
            this.requestId++;
            this.query = '';
            this.media = 0;
            this.results = [];
            this.total = 0;
            if (this.panelOpen) this.searchProducts();
        },
    },
    mounted() {
        document.addEventListener('mousedown', this.handleOutside);
        API.getPlatList().then(res => { this.mediaOptions = res.data || []; });
    },
    beforeDestroy() {
        clearTimeout(this.searchTimer);
        document.removeEventListener('mousedown', this.handleOutside);
    },
    methods: {
        identity(product) { return `${product.product_source}:${product.product_plat_id}:${product.product_key}`; },
        meta(product) {
            return `${product.product_plat_name || '-'}｜ID ${product.display_id || product.product_key}`;
        },
        isSelected(product) { return this.value.some(selected => this.identity(selected) === this.identity(product)); },
        openPanel() {
            if (this.disabled) return;
            this.panelOpen = true;
            if (!this.results.length && !this.loading) this.searchProducts();
        },
        handleOutside(event) {
            if (!this.$el.contains(event.target) &&
                !event.target.closest('.work-order-product-media-options')) this.panelOpen = false;
        },
        queueSearch() {
            clearTimeout(this.searchTimer);
            this.requestId++;
            this.results = [];
            this.total = 0;
            this.loading = true;
            this.panelOpen = true;
            this.searchTimer = setTimeout(() => this.searchProducts(), 250);
        },
        changeMedia() {
            clearTimeout(this.searchTimer);
            this.searchProducts();
        },
        async searchProducts() {
            const requestId = ++this.requestId;
            this.results = [];
            this.total = 0;
            this.loading = true;
            try {
                const res = await API.getProductList({ project_ids: [this.projectId], search: this.query,
                    product_plat_id: this.media || 0, limit: 200 });
                if (requestId !== this.requestId) return;
                const data = (res.data || [])[0] || {};
                this.results = data.products || [];
                this.total = data.total || 0;
            } catch (error) {
                if (requestId === this.requestId) this.$message.error(error.info || '产品查询失败');
            } finally {
                if (requestId === this.requestId) this.loading = false;
            }
        },
        toggleProduct(product, checked) {
            if (this.disabled) return;
            const key = this.identity(product);
            const products = this.value.filter(selected => this.identity(selected) !== key);
            if (checked) products.push(product);
            this.$emit('input', products);
        },
        selectAll() {
            if (this.disabled) return;
            const chosen = new Map(this.value.map(product => [this.identity(product), product]));
            this.results.forEach(product => chosen.set(this.identity(product), product));
            this.$emit('input', [...chosen.values()]);
        },
        unselectAll() {
            if (this.disabled) return;
            const current = new Set(this.results.map(this.identity));
            this.$emit('input', this.value.filter(product => !current.has(this.identity(product))));
        },
        removeProduct(product) { this.toggleProduct(product, false); },
        handleBatch() {
            if (this.disabled) return;
            this.panelOpen = false;
            new BaseDialog({
                component: () => import('./productBatchModal.vue'),
                options: { title: '批量选择产品', width: '600px', closeOnClickModal: false },
                cbs: {
                    onSearch: async terms => {
                        const res = await API.getProductList({ project_ids: [this.projectId], batch_search: terms, limit: 200 });
                        return (res.data || [])[0] || {};
                    },
                    onApply: products => {
                        const chosen = new Map(this.value.map(product => [this.identity(product), product]));
                        products.forEach(product => chosen.set(this.identity(product), product));
                        this.$emit('input', [...chosen.values()]);
                    },
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.product-select { min-width: 0; }
.search-line { display: flex; height: 30px; border: 1px solid #dcdfe6; border-radius: 4px; box-sizing: border-box; }
.query-input { flex: 1; min-width: 0; }
::v-deep .query-input .el-input__inner { height: 28px; line-height: 28px; border: 0; }
.batch-button { flex-shrink: 0; width: 30px; padding: 0; border-left: 1px solid #dcdfe6; border-radius: 0; }
.result-panel { margin-top: 4px; padding: 8px; border: 1px solid #dcdfe6; border-radius: 4px; background: #fff; }
.selected-block { margin-top: 8px; border: 1px solid #ebeef5; border-radius: 4px; }
.selected-header { padding: 4px 8px; color: #606266; font-size: 12px; line-height: 20px; background: #f5f7fa; }
.selected-list { max-height: 132px; overflow-y: auto; }
.selected-item { display: flex; align-items: center; min-width: 0; min-height: 42px; padding: 4px 8px; }
.selected-item + .selected-item { border-top: 1px solid #f2f3f5; }
.selected-item .product-text { display: flex; flex: 1; flex-direction: column; min-width: 0; line-height: 18px; }
.selected-item .product-name, .selected-item .product-meta { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.selected-item .product-details { display: flex; min-width: 0; color: #909399; font-size: 12px; }
.selected-item .product-meta { flex-shrink: 0; max-width: 45%; }
.selected-item .product-package { min-width: 0; margin-left: 8px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.selected-item .el-button { flex-shrink: 0; margin-left: 6px; }
</style>

<style lang="scss">
.product-select .result-panel {
    .panel-header, .result-actions { display: flex; align-items: center; justify-content: space-between; }
    .panel-header { min-height: 30px; font-weight: 500; }
    .media-filter { width: 110px; }
    .result-actions { justify-content: flex-start; gap: 12px; }
    .result-list { max-height: 188px; overflow-y: auto; border-top: 1px solid #ebeef5; border-bottom: 1px solid #ebeef5; }
    .product-row { display: flex; align-items: center; min-width: 0; min-height: 42px; padding: 4px 6px; box-sizing: border-box; }
    .product-row + .product-row { border-top: 1px solid #f2f3f5; }
    .product-row:hover { background: #f5f7fa; }
    .product-row .el-checkbox { display: flex; align-items: center; width: 100%; min-width: 0; margin-right: 0; }
    .product-row .el-checkbox__label { flex: 1; min-width: 0; }
    .product-text { display: flex; flex-direction: column; min-width: 0; flex: 1; line-height: 18px; }
    .product-name, .product-meta, .product-package { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .product-details { display: flex; min-width: 0; color: #909399; font-size: 12px; }
    .product-meta { flex-shrink: 0; max-width: 45%; }
    .product-package { min-width: 0; margin-left: 8px; }
    .product-meta, .result-tip, .empty { color: #909399; font-size: 12px; }
    .product-row .el-button { flex-shrink: 0; margin-left: 6px; }
    .result-tip { padding: 4px 6px 0; }
    .empty { padding: 14px 6px; text-align: center; }
}
</style>
